import React, { useState, useEffect } from "react";
import { useUserAuth } from "../context/userAuthContext";
import { db } from "../firebase";
import {
  collection,
  query,
  getDocs,
  where,
  orderBy,
  limit,
} from "firebase/firestore";
import {
  BarChart3,
  ShoppingCart,
  TrendingUp,
  Boxes,
  AlertTriangle,
  Package,
  Calendar,
  Clock,
  RefreshCw,
  ArrowRightCircle,
  DollarSign,
  Truck,
  Shield,
  Clock3,
  Menu,
  X,
  Home,
  ShoppingBag,
  Users,
} from "lucide-react";

// Import the NavbarComponent

const Dashboard = () => {
  const { user } = useUserAuth();
  const [loading, setLoading] = useState(true);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [stats, setStats] = useState({
    pendingOrders: 0,
    lowStockItems: 0,
    totalSales: 0,
    nextDelivery: "Hoy, 15:30",
  });
  const [recentOrders, setRecentOrders] = useState([]);
  const [activeTab, setActiveTab] = useState("overview");

  useEffect(() => {
    const fetchDashboardData = async () => {
      if (user?.uid) {
        try {
          // This would be replaced with your actual Firebase queries
          // Sample data for demonstration
          setStats({
            pendingOrders: 8,
            lowStockItems: 5,
            totalSales: 24500,
            nextDelivery: "Hoy, 15:30",
          });

          setRecentOrders([
            {
              id: "ORD-7829",
              supplier: "Distribuidora González",
              items: ["Vino tinto", "Aceite de oliva", "Queso manchego"],
              status: "En camino",
              total: 4850,
              eta: "Hoy, 15:30",
            },
            {
              id: "ORD-7828",
              supplier: "Carnes Premium",
              items: ["Filete de res", "Pollo orgánico", "Cerdo ibérico"],
              status: "Procesando",
              total: 6200,
              eta: "Mañana, 10:00",
            },
            {
              id: "ORD-7827",
              supplier: "Frutas y Verduras S.A.",
              items: ["Tomates", "Lechugas", "Aguacates", "Limones"],
              status: "Entregado",
              total: 1850,
              eta: "Ayer, 16:45",
            },
          ]);

          setLoading(false);
        } catch (error) {
          console.error("Error fetching dashboard data:", error);
          setLoading(false);
        }
      }
    };

    fetchDashboardData();
  }, [user]);

  const getStatusClass = (status) => {
    switch (status) {
      case "En camino":
        return "bg-blue-100 text-blue-800";
      case "Procesando":
        return "bg-yellow-100 text-yellow-800";
      case "Entregado":
        return "bg-green-100 text-green-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  // Function to show a loading spinner
  const LoadingSpinner = () => (
    <div className="flex justify-center items-center h-screen">
      <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500"></div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* The NavbarComponent would be rendered here in a real app */}
      {/* <NavbarComponent /> */}

      {loading ? (
        <LoadingSpinner />
      ) : (
        <div className="pt-20 pb-10 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            {/* Dashboard Header */}
            <div className="mb-8">
              <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900">
                Bienvenido a Vine Business
              </h1>
              <p className="text-sm sm:text-base text-gray-600">
                Gestiona tu inventario, procesa pedidos y optimiza tu negocio
              </p>
            </div>

            {/* Mobile Menu Button */}
            <div className="block md:hidden mb-4">
              <button
                type="button"
                className="flex items-center justify-center p-2 rounded-md text-purple-600 hover:text-purple-800 hover:bg-purple-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-purple-500"
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              >
                <span className="sr-only">Abrir menú</span>
                {isMobileMenuOpen ? (
                  <X className="block h-6 w-6" aria-hidden="true" />
                ) : (
                  <Menu className="block h-6 w-6" aria-hidden="true" />
                )}
              </button>
            </div>

            {/* Tabs - Desktop */}
            <div className="hidden md:block mb-6 border-b border-gray-200">
              <nav className="flex space-x-8">
                <button
                  onClick={() => setActiveTab("overview")}
                  className={`py-4 px-1 border-b-2 font-medium text-sm ${
                    activeTab === "overview"
                      ? "border-purple-600 text-purple-600"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }`}
                >
                  Vista General
                </button>
                <button
                  onClick={() => setActiveTab("inventory")}
                  className={`py-4 px-1 border-b-2 font-medium text-sm ${
                    activeTab === "inventory"
                      ? "border-purple-600 text-purple-600"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }`}
                >
                  Inventario
                </button>
                <button
                  onClick={() => setActiveTab("orders")}
                  className={`py-4 px-1 border-b-2 font-medium text-sm ${
                    activeTab === "orders"
                      ? "border-purple-600 text-purple-600"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }`}
                >
                  Pedidos
                </button>
                <button
                  onClick={() => setActiveTab("suppliers")}
                  className={`py-4 px-1 border-b-2 font-medium text-sm ${
                    activeTab === "suppliers"
                      ? "border-purple-600 text-purple-600"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }`}
                >
                  Proveedores
                </button>
              </nav>
            </div>

            {/* Mobile Menu Drawer */}
            {isMobileMenuOpen && (
              <div className="md:hidden mb-6 bg-white rounded-lg shadow-lg p-4">
                <nav className="grid grid-cols-1 gap-2">
                  <button
                    onClick={() => {
                      setActiveTab("overview");
                      setIsMobileMenuOpen(false);
                    }}
                    className={`flex items-center py-3 px-4 rounded-md text-base font-medium ${
                      activeTab === "overview"
                        ? "bg-purple-100 text-purple-800"
                        : "text-gray-600 hover:bg-gray-50"
                    }`}
                  >
                    <Home className="mr-3 h-5 w-5" />
                    Vista General
                  </button>
                  <button
                    onClick={() => {
                      setActiveTab("inventory");
                      setIsMobileMenuOpen(false);
                    }}
                    className={`flex items-center py-3 px-4 rounded-md text-base font-medium ${
                      activeTab === "inventory"
                        ? "bg-purple-100 text-purple-800"
                        : "text-gray-600 hover:bg-gray-50"
                    }`}
                  >
                    <Boxes className="mr-3 h-5 w-5" />
                    Inventario
                  </button>
                  <button
                    onClick={() => {
                      setActiveTab("orders");
                      setIsMobileMenuOpen(false);
                    }}
                    className={`flex items-center py-3 px-4 rounded-md text-base font-medium ${
                      activeTab === "orders"
                        ? "bg-purple-100 text-purple-800"
                        : "text-gray-600 hover:bg-gray-50"
                    }`}
                  >
                    <ShoppingBag className="mr-3 h-5 w-5" />
                    Pedidos
                  </button>
                  <button
                    onClick={() => {
                      setActiveTab("suppliers");
                      setIsMobileMenuOpen(false);
                    }}
                    className={`flex items-center py-3 px-4 rounded-md text-base font-medium ${
                      activeTab === "suppliers"
                        ? "bg-purple-100 text-purple-800"
                        : "text-gray-600 hover:bg-gray-50"
                    }`}
                  >
                    <Users className="mr-3 h-5 w-5" />
                    Proveedores
                  </button>
                </nav>
              </div>
            )}

            {activeTab === "overview" && (
              <>
                {/* Stats Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-8">
                  {/* Pending Orders */}
                  <div className="bg-white overflow-hidden shadow rounded-lg">
                    <div className="p-5">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 bg-purple-100 rounded-md p-3">
                          <ShoppingCart className="h-6 w-6 text-purple-600" />
                        </div>
                        <div className="ml-5 w-0 flex-1">
                          <dl>
                            <dt className="text-sm font-medium text-gray-500 truncate">
                              Pedidos Pendientes
                            </dt>
                            <dd>
                              <div className="text-lg font-medium text-gray-900">
                                {stats.pendingOrders}
                              </div>
                            </dd>
                          </dl>
                        </div>
                      </div>
                    </div>
                    <div className="bg-gray-50 px-5 py-3">
                      <div className="text-sm">
                        <a
                          href="#"
                          className="font-medium text-purple-700 hover:text-purple-900 flex items-center"
                        >
                          Ver todos
                          <ArrowRightCircle className="ml-1 h-4 w-4" />
                        </a>
                      </div>
                    </div>
                  </div>

                  {/* Low Stock Items */}
                  <div className="bg-white overflow-hidden shadow rounded-lg">
                    <div className="p-5">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 bg-orange-100 rounded-md p-3">
                          <AlertTriangle className="h-6 w-6 text-orange-600" />
                        </div>
                        <div className="ml-5 w-0 flex-1">
                          <dl>
                            <dt className="text-sm font-medium text-gray-500 truncate">
                              Stock Bajo
                            </dt>
                            <dd>
                              <div className="text-lg font-medium text-gray-900">
                                {stats.lowStockItems} productos
                              </div>
                            </dd>
                          </dl>
                        </div>
                      </div>
                    </div>
                    <div className="bg-gray-50 px-5 py-3">
                      <div className="text-sm">
                        <a
                          href="#"
                          className="font-medium text-purple-700 hover:text-purple-900 flex items-center"
                        >
                          Reabastecer
                          <RefreshCw className="ml-1 h-4 w-4" />
                        </a>
                      </div>
                    </div>
                  </div>

                  {/* Total Sales */}
                  <div className="bg-white overflow-hidden shadow rounded-lg">
                    <div className="p-5">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 bg-green-100 rounded-md p-3">
                          <BarChart3 className="h-6 w-6 text-green-600" />
                        </div>
                        <div className="ml-5 w-0 flex-1">
                          <dl>
                            <dt className="text-sm font-medium text-gray-500 truncate">
                              Ventas Totales
                            </dt>
                            <dd>
                              <div className="text-lg font-medium text-gray-900">
                                ${stats.totalSales.toLocaleString()}
                              </div>
                            </dd>
                          </dl>
                        </div>
                      </div>
                    </div>
                    <div className="bg-gray-50 px-5 py-3">
                      <div className="text-sm">
                        <a
                          href="#"
                          className="font-medium text-purple-700 hover:text-purple-900 flex items-center"
                        >
                          Ver reportes
                          <TrendingUp className="ml-1 h-4 w-4" />
                        </a>
                      </div>
                    </div>
                  </div>

                  {/* Next Delivery */}
                  <div className="bg-white overflow-hidden shadow rounded-lg">
                    <div className="p-5">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 bg-blue-100 rounded-md p-3">
                          <Truck className="h-6 w-6 text-blue-600" />
                        </div>
                        <div className="ml-5 w-0 flex-1">
                          <dl>
                            <dt className="text-sm font-medium text-gray-500 truncate">
                              Próxima Entrega
                            </dt>
                            <dd>
                              <div className="text-lg font-medium text-gray-900">
                                {stats.nextDelivery}
                              </div>
                            </dd>
                          </dl>
                        </div>
                      </div>
                    </div>
                    <div className="bg-gray-50 px-5 py-3">
                      <div className="text-sm">
                        <a
                          href="#"
                          className="font-medium text-purple-700 hover:text-purple-900 flex items-center"
                        >
                          Ver calendario
                          <Calendar className="ml-1 h-4 w-4" />
                        </a>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Recent Orders */}
                <div className="bg-white shadow rounded-lg mb-8">
                  <div className="px-4 py-5 sm:px-6 flex flex-col sm:flex-row justify-between items-start sm:items-center">
                    <h2 className="text-lg leading-6 font-medium text-gray-900 mb-2 sm:mb-0">
                      Pedidos Recientes
                    </h2>
                    <a
                      href="#"
                      className="text-sm font-medium text-purple-600 hover:text-purple-500 flex items-center"
                    >
                      Ver todos los pedidos
                      <ArrowRightCircle className="ml-1 h-4 w-4" />
                    </a>
                  </div>
                  <div className="border-t border-gray-200 divide-y divide-gray-200">
                    {recentOrders.map((order) => (
                      <div
                        key={order.id}
                        className="px-4 py-4 sm:px-6 hover:bg-gray-50"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center">
                            <div className="min-w-0 flex-1">
                              <p className="text-sm font-medium text-purple-800">
                                {order.id}
                              </p>
                              <p className="text-sm text-gray-500 truncate">
                                {order.supplier}
                              </p>
                            </div>
                          </div>
                          <div className="ml-2 flex-shrink-0 flex">
                            <p
                              className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusClass(
                                order.status
                              )}`}
                            >
                              {order.status}
                            </p>
                          </div>
                        </div>
                        <div className="mt-2 flex flex-col sm:flex-row sm:justify-between">
                          <div className="flex">
                            <p className="flex items-center text-sm text-gray-500">
                              <Package className="flex-shrink-0 mr-1.5 h-4 w-4 text-gray-400" />
                              {order.items.slice(0, 2).join(", ")}
                              {order.items.length > 2 &&
                                ` +${order.items.length - 2} más`}
                            </p>
                          </div>
                          <div className="mt-2 flex items-center justify-between text-sm text-gray-500 sm:mt-0">
                            <div className="flex items-center">
                              <Clock className="flex-shrink-0 mr-1.5 h-4 w-4 text-gray-400" />
                              <p>ETA: {order.eta}</p>
                            </div>
                            <p className="ml-4 font-medium text-gray-900">
                              ${order.total.toLocaleString()}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Why Choose Vine Section */}
                <div className="bg-gradient-to-r from-purple-800 to-purple-600 rounded-lg shadow-lg p-4 sm:p-6 text-white mb-8">
                  <h2 className="text-lg sm:text-xl font-bold mb-4 sm:mb-6 text-center sm:text-left">
                    ¿Por qué elegir Vine?
                  </h2>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-6">
                    <div className="flex flex-col items-center text-center p-3 sm:p-4 bg-white/10 rounded-lg hover:bg-white/20 transition-all">
                      <DollarSign className="h-8 w-8 sm:h-10 sm:w-10 mb-1 sm:mb-2" />
                      <h3 className="font-semibold text-base sm:text-lg">
                        Mejores precios
                      </h3>
                      <p className="text-purple-100 text-xs sm:text-sm">
                        Precios mayoristas competitivos
                      </p>
                    </div>
                    <div className="flex flex-col items-center text-center p-3 sm:p-4 bg-white/10 rounded-lg hover:bg-white/20 transition-all">
                      <Clock3 className="h-8 w-8 sm:h-10 sm:w-10 mb-1 sm:mb-2" />
                      <h3 className="font-semibold text-base sm:text-lg">
                        Ahorra tiempo
                      </h3>
                      <p className="text-purple-100 text-xs sm:text-sm">
                        Automatización de pedidos
                      </p>
                    </div>
                    <div className="flex flex-col items-center text-center p-3 sm:p-4 bg-white/10 rounded-lg hover:bg-white/20 transition-all">
                      <Shield className="h-8 w-8 sm:h-10 sm:w-10 mb-1 sm:mb-2" />
                      <h3 className="font-semibold text-base sm:text-lg">
                        Garantía
                      </h3>
                      <p className="text-purple-100 text-xs sm:text-sm">
                        Calidad asegurada
                      </p>
                    </div>
                    <div className="flex flex-col items-center text-center p-3 sm:p-4 bg-white/10 rounded-lg hover:bg-white/20 transition-all">
                      <Truck className="h-8 w-8 sm:h-10 sm:w-10 mb-1 sm:mb-2" />
                      <h3 className="font-semibold text-base sm:text-lg">
                        Rápido
                      </h3>
                      <p className="text-purple-100 text-xs sm:text-sm">
                        Entregas express
                      </p>
                    </div>
                  </div>
                </div>

                {/* Feature Cards */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                  {/* Register Restaurant Card */}
                  <div className="bg-white shadow rounded-lg overflow-hidden transform hover:scale-102 hover:shadow-lg transition-all">
                    <div className="p-4 sm:p-6">
                      <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-2">
                        Registra tu restaurante
                      </h3>
                      <p className="text-sm text-gray-600 mb-3 sm:mb-4">
                        Optimiza tu inventario y accede a los mejores precios en
                        insumos.
                      </p>
                      <ul className="mb-4 sm:mb-6 space-y-1 sm:space-y-2">
                        <li className="flex items-center text-sm text-gray-600">
                          <span className="h-3 w-3 sm:h-4 sm:w-4 mr-2 text-purple-500">
                            •
                          </span>
                          Precios mayoristas
                        </li>
                        <li className="flex items-center text-sm text-gray-600">
                          <span className="h-3 w-3 sm:h-4 sm:w-4 mr-2 text-purple-500">
                            •
                          </span>
                          Gestión de inventario
                        </li>
                        <li className="flex items-center text-sm text-gray-600">
                          <span className="h-3 w-3 sm:h-4 sm:w-4 mr-2 text-purple-500">
                            •
                          </span>
                          Alertas automáticas
                        </li>
                      </ul>
                      <button className="w-full bg-purple-600 hover:bg-purple-700 text-white font-bold py-2 px-4 rounded transition duration-150 ease-in-out transform active:scale-95">
                        ¡Empieza ahora!
                      </button>
                    </div>
                  </div>

                  {/* Sell Products Card */}
                  <div className="bg-white shadow rounded-lg overflow-hidden transform hover:scale-102 hover:shadow-lg transition-all">
                    <div className="p-4 sm:p-6">
                      <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-2">
                        Vende tus productos
                      </h3>
                      <p className="text-sm text-gray-600 mb-3 sm:mb-4">
                        Conecta con restaurantes y vende tus productos al
                        mayoreo.
                      </p>
                      <ul className="mb-4 sm:mb-6 space-y-1 sm:space-y-2">
                        <li className="flex items-center text-sm text-gray-600">
                          <span className="h-3 w-3 sm:h-4 sm:w-4 mr-2 text-purple-500">
                            •
                          </span>
                          Acceso a miles de compradores
                        </li>
                        <li className="flex items-center text-sm text-gray-600">
                          <span className="h-3 w-3 sm:h-4 sm:w-4 mr-2 text-purple-500">
                            •
                          </span>
                          Pagos seguros
                        </li>
                        <li className="flex items-center text-sm text-gray-600">
                          <span className="h-3 w-3 sm:h-4 sm:w-4 mr-2 text-purple-500">
                            •
                          </span>
                          Dashboard de ventas
                        </li>
                      </ul>
                      <button className="w-full bg-purple-600 hover:bg-purple-700 text-white font-bold py-2 px-4 rounded transition duration-150 ease-in-out transform active:scale-95">
                        Comienza a vender
                      </button>
                    </div>
                  </div>
                </div>
              </>
            )}

            {/* Inventory Tab Content - Just a placeholder */}
            {activeTab === "inventory" && (
              <div className="bg-white shadow rounded-lg p-4 sm:p-6">
                <h2 className="text-lg font-medium text-gray-900 mb-4">
                  Inventario
                </h2>
                <p className="text-sm sm:text-base text-gray-600 mb-4">
                  Aquí podrás gestionar todo tu inventario, categorías de
                  productos, alertas de stock bajo, y más.
                </p>
                {/* Inventory content would go here */}
                <div className="h-64 sm:h-96 flex items-center justify-center bg-gray-50 rounded-lg">
                  <div className="text-center p-4">
                    <Boxes className="h-12 w-12 text-purple-400 mx-auto mb-4" />
                    <p className="text-gray-500 text-sm sm:text-base">
                      Contenido de Inventario en desarrollo
                    </p>
                    <button className="mt-4 bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium py-2 px-4 rounded transition-all">
                      Configurar Inventario
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Orders Tab Content - Just a placeholder */}
            {activeTab === "orders" && (
              <div className="bg-white shadow rounded-lg p-4 sm:p-6">
                <h2 className="text-lg font-medium text-gray-900 mb-4">
                  Pedidos
                </h2>
                <p className="text-sm sm:text-base text-gray-600 mb-4">
                  Gestiona todos tus pedidos a proveedores, historial de
                  compras, y programa pedidos automáticos.
                </p>
                {/* Orders content would go here */}
                <div className="h-64 sm:h-96 flex items-center justify-center bg-gray-50 rounded-lg">
                  <div className="text-center p-4">
                    <ShoppingBag className="h-12 w-12 text-purple-400 mx-auto mb-4" />
                    <p className="text-gray-500 text-sm sm:text-base">
                      Contenido de Pedidos en desarrollo
                    </p>
                    <button className="mt-4 bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium py-2 px-4 rounded transition-all">
                      Crear Nuevo Pedido
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Suppliers Tab Content - Just a placeholder */}
            {activeTab === "suppliers" && (
              <div className="bg-white shadow rounded-lg p-4 sm:p-6">
                <h2 className="text-lg font-medium text-gray-900 mb-4">
                  Proveedores
                </h2>
                <p className="text-sm sm:text-base text-gray-600 mb-4">
                  Encuentra nuevos proveedores, compara precios y gestiona tus
                  relaciones comerciales.
                </p>
                {/* Suppliers content would go here */}
                <div className="h-64 sm:h-96 flex items-center justify-center bg-gray-50 rounded-lg">
                  <div className="text-center p-4">
                    <Users className="h-12 w-12 text-purple-400 mx-auto mb-4" />
                    <p className="text-gray-500 text-sm sm:text-base">
                      Contenido de Proveedores en desarrollo
                    </p>
                    <button className="mt-4 bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium py-2 px-4 rounded transition-all">
                      Agregar Proveedor
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
